import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import type { ConversationFlag_State } from '@/api/generated';
import { conversationKeys } from './useConversations';

export const conversationFlagKeys = {
  all: ['conversation-flags'] as const,
  list: (state: ConversationFlag_State | undefined, conversationId: string, pageSize: number, pageToken: string) =>
    [...conversationFlagKeys.all, 'list', state ?? '', conversationId, pageSize, pageToken] as const,
};

/**
 * A page of flags, newest first. No state lists every state, and no
 * conversation lists every conversation's.
 */
export function useConversationFlags(params: {
  state?: ConversationFlag_State;
  conversationId?: string;
  pageSize: number;
  pageToken?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: conversationFlagKeys.list(
      params.state,
      params.conversationId ?? '',
      params.pageSize,
      params.pageToken ?? '',
    ),
    queryFn: () =>
      adminClient.listConversationFlags({
        state: params.state,
        conversationId: params.conversationId || undefined,
        pageSize: params.pageSize,
        pageToken: params.pageToken || undefined,
      }),
    enabled: params.enabled ?? true,
  });
}

// A flag changes a conversation's open-flag count, which the conversations
// list and the conversation's own page both show, so both are refetched.
function useInvalidateFlags() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: conversationFlagKeys.all });
    queryClient.invalidateQueries({ queryKey: conversationKeys.all });
  };
}

/** Flags a message, or the whole conversation when messageId is omitted, as the signed-in admin. */
export function useCreateConversationFlag() {
  const invalidate = useInvalidateFlags();
  return useMutation({
    mutationFn: ({
      conversationId,
      ...data
    }: {
      conversationId: string;
      messageId?: string;
      feedback: string;
      idempotencyKey: string;
    }) => adminClient.createConversationFlag(conversationId, data),
    onSuccess: invalidate,
  });
}

/** Moves a flag through review: open, reviewed, actioned. */
export function useUpdateConversationFlag() {
  const invalidate = useInvalidateFlags();
  return useMutation({
    mutationFn: ({ flagId, state }: { flagId: string; state: ConversationFlag_State }) =>
      adminClient.updateConversationFlag(flagId, { state }),
    onSuccess: invalidate,
  });
}
